import { useParams } from 'react-router';
import { businessDate, money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { PrintDocument } from '../payments/kit/PrintDocument';
import { BackLink, DetailSkeleton, ErrorState } from '../payments/kit/ui';
import { useNote, type Note, type NoteKind } from './api';

/** A4 credit note / debit note. */
export function NotePrintPage({ kind }: { kind: NoteKind }) {
  const { id = '' } = useParams();
  const { format } = useShop();
  const note = useNote(kind, id);
  const back = `/returns/${kind}-notes/${id}`;

  if (note.isLoading) return <DetailSkeleton />;
  if (note.isError || !note.data)
    return (
      <>
        <BackLink to={back} label="Back" />
        <ErrorState error={note.error} onRetry={() => void note.refetch()} />
      </>
    );

  const n = note.data;
  return (
    <PrintDocument<Note['lines'][number]>
      title={kind === 'credit' ? 'Credit note' : 'Debit note'}
      number={n.number}
      status={n.status === 'CANCELLED' ? 'Cancelled' : undefined}
      backTo={back}
      party={{
        label: kind === 'credit' ? 'Customer' : 'Supplier',
        name: n.contact.name,
        lines: [n.contact.phone],
      }}
      meta={[
        { label: 'Date', value: businessDate(n.docDate, format) },
        {
          label: kind === 'credit' ? 'Invoice' : 'Bill',
          value: n.source.extra ? `${n.source.number} (${n.source.extra})` : n.source.number,
        },
        ...(n.reason ? [{ label: 'Reason', value: n.reason }] : []),
      ]}
      columns={[
        { key: 'no', header: '#', width: '6%', render: (l) => l.lineNo },
        { key: 'desc', header: 'Description', render: (l) => l.description },
        {
          key: 'kind',
          header: 'Type',
          render: (l) => (l.qtyBase !== null ? 'Returned goods' : 'Price adjustment'),
        },
        { key: 'net', header: 'Net', align: 'right', render: (l) => money(l.netAmount, format) },
        { key: 'tax', header: 'Tax', align: 'right', render: (l) => money(l.taxAmount, format) },
        {
          key: 'total',
          header: 'Total',
          align: 'right',
          render: (l) => money(l.lineTotal, format),
        },
      ]}
      rows={n.lines}
      rowKey={(l) => l.id}
      totals={[
        { label: 'Net', value: money(n.netTotal, format) },
        { label: 'Tax', value: money(n.taxTotal, format) },
        {
          label: kind === 'credit' ? 'Total credit' : 'Total debit',
          value: money(n.total, format),
          strong: true,
        },
      ]}
      notes={[n.notes, n.cancelReason ? `Cancelled: ${n.cancelReason}` : null]}
    />
  );
}
